import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Custom Deck Design',
    description: 'Personalized deck designs to match your home and lifestyle.'
  },
  {
    title: 'Patio Installation',
    description: 'Professional installation of patios using quality materials.'
  },
  {
    title: 'Pergola Building',
    description: 'Custom pergolas to create shaded outdoor living spaces.'
  },
  {
    title: 'Outdoor Kitchens',
    description: 'Functional outdoor cooking and entertaining spaces.'
  },
  {
    title: 'Fire Pit Areas',
    description: 'Custom fire pit installations for year-round enjoyment.'
  },
  {
    title: 'Lighting Systems',
    description: 'Outdoor lighting for ambiance and safety.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1591825729269-caeb344f6df2?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Custom Deck'
  },
  {
    url: 'https://images.unsplash.com/photo-1598902108854-10e335adac99?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Patio'
  },
  {
    url: 'https://images.unsplash.com/photo-1624372646014-205dde2e9ba1?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Outdoor Kitchen'
  }
];

export function DeckPatioPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Deck & Patio Construction in Westchester County | Golden Touch</title>
        <meta name="description" content="Professional deck and patio construction services in Westchester County & Greenwich. Create beautiful outdoor living spaces with quality craftsmanship." />
        <meta name="keywords" content="deck construction Westchester, patio builder Greenwich, outdoor living spaces Scarsdale, deck contractor" />
        <link rel="canonical" href="https://renovationwestchester.com/services/deck-patio-construction" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Deck & Patio Construction in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Create beautiful outdoor living spaces with our custom deck and patio construction services. 
                From simple decks to complete outdoor entertainment areas, we bring quality craftsmanship 
                to every project.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Deck Construction in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Create Your Outdoor Space?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your outdoor living project. Our team is ready to help create 
              the perfect space for relaxation and entertainment.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}